import React, { useMemo } from 'react';

import { Type } from '@deqo/foundation';
import ace, { Ace, Range } from 'ace-builds';

import 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/src-noconflict/mode-java';
import { Mode } from 'ace-builds/src-noconflict/mode-text';
import 'ace-builds/src-noconflict/theme-cloud_editor';
import { CodeEditor, CodeEditorProps } from './CodeEditor';

const TextHighlightRules = ace.require('ace/mode/text_highlight_rules').TextHighlightRules as Type<Ace.HighlightRules>;
const TextMode = Mode as Type<Ace.SyntaxMode>;

class MflHighlightRules extends TextHighlightRules {

    $rules = {} as any;

    constructor() {
        super();

        // regexp must not have capturing parentheses. Use (?:) instead.
        // regexps are ordered -> the first match is used

        this.$rules = {
            'start': [
                {
                    token: 'string', // character
                    regex: /'(?:.|\\(:?u[\da-fA-F]+|x[\da-fA-F]+|[tbrf'"n]))?'/
                }, {
                    token: 'string', start: '"', end: '"|$', next: [
                        { token: 'constant.language.escape', regex: /\\(:?u[\da-fA-F]+|x[\da-fA-F]+|[tbrf'"n])/ },
                        { token: 'invalid', regex: /\\./ }
                    ]
                }, {
                    token: 'constant.numeric', // hex
                    regex: '0[xX][0-9a-fA-F]+\\b'
                }, {
                    token: 'constant.numeric', // float
                    regex: '[+-]?\\d+(?:(?:\\.\\d*)?(?:[eE][+-]?\\d+)?)?\\b'
                }, {
                    token: 'constant.language.boolean',
                    regex: '(?:true|false)\\b'
                }, {
                    token: 'identifier',
                    regex: '[a-zA-Z_$][a-zA-Z0-9_$]*\\('
                }, {
                    token: 'keyword',
                    regex: '[a-zA-Z_$][a-zA-Z0-9_$]*\\b'
                }, {
                    token: 'keyword.operator',
                    regex: '!|\\$|%|&|\\*|\\-\\-|\\-|\\+\\+|\\+|~|===|==|=|!=|!==|<=|>=|<<=|>>=|>>>=|<>|<|>|!|&&|\\|\\||\\?\\:|\\*=|%=|\\+=|\\-=|&=|\\^=|\\b(?:in|instanceof|new|delete|typeof|void)'
                }, {
                    token: 'keyword',
                    regex: '^\\s*#(if|else|elif|endif|define|undef|warning|error|line|region|endregion|pragma)'
                }, {
                    token: 'punctuation.operator',
                    regex: '\\?|\\:|\\,|\\;|\\.'
                }, {
                    token: 'paren.lparen',
                    regex: '[[({]'
                }, {
                    token: 'paren.rparen',
                    regex: '[\\])}]'
                }, {
                    token: 'text',
                    regex: '\\s+'
                }
            ]
        };

        this.normalizeRules();
    }
}

class MflMode extends TextMode {

    constructor() {
        super();
        this.HighlightRules = MflHighlightRules;
    }
}

const createItem = (name: string): Ace.Completion => {
    return {
        caption: name,
        value: name,
        meta: '',
        score: 100
    };
};


class MyCompleter implements Ace.Completer {
    getCompletions(editor: Ace.Editor, session: Ace.EditSession, pos: Ace.Point, prefix: string, callback: Ace.CompleterCallback) {
        const range = Range.fromPoints({ column: 0, row: 0 }, pos);
        console.log(session.getTextRange(range));
        callback(null, [
            createItem('POW'),
            createItem('PI'),
            createItem('SUM'),
            createItem('MIN'),
            createItem('MAX'),
            createItem('AVG'),
            createItem('ROUNDTO'),
            createItem('ROUND'),
            createItem('FLOOR'),
            createItem('CEIL'),
            createItem('ABS'),
            createItem('SQRT'),
            createItem('SIN'),
            createItem('COS'),
            createItem('TAN'),
        ]);
    }
}

export type MflEditorProps = Omit<CodeEditorProps, 'mode'>;

export const MflEditor = (props: MflEditorProps) => {
    const mode = useMemo(() => new MflMode(), []);
    return <CodeEditor mode={mode} {...props} />;
};
